import { ref, reactive, onMounted, onUnmounted, computed } from 'vue'
import { type CustomerForm, type CustomerQuestionnaire } from '@/types/generated/types.gen'
import { type PdfAnnotation } from '@/store/newPdfStore'
import mitt from 'mitt'

// Define event types
type EmailEvents = {
  'email-sent': any
  'template-updated': any
}

// Create an event emitter for email events
const emitter = mitt<EmailEvents>()

// Define types
export interface EmailReplyData {
  subject: string
  to: string
}

// Interface for email template
export interface EmailTemplate {
  uuid: string
  name: string
  subject: string
  html: string
  type?: string
  [key: string]: any
}

// Interface for attached document
export enum DocumentTypeIdEnum {
  PDF_TEMPLATE = 'PDF_TEMPLATE',
  QUESTIONNAIRE = 'QUESTIONNAIRE'
}

export type DocumentTypeRequest = {
  id: DocumentTypeIdEnum
  name: 'Dokumentenvorlage' | 'Fragebogen'
}

export type FormDocumentProperties = CustomerForm & {
  annotationData: PdfAnnotation[]
  signatures: Record<string, any>
}

export interface QuestionnaireDocumentProperties extends CustomerQuestionnaire {
  id: string
}

export type DocumentProperties = FormDocumentProperties | QuestionnaireDocumentProperties

export interface AttachedDocument {
  documentType: DocumentTypeRequest
  documentProperties: DocumentProperties
}

// Create state that will be shared across components
const isOpen = ref(false)
const isMinimized = ref(false)
const isMaximized = ref(false)
const replyData = ref<EmailReplyData | null>(null)
const popoverPosition = reactive({ x: 0, y: 0 })
const popoverSize = reactive({ width: 580, height: 650 })

// Email content state
const emailSubject = ref('')
const emailContent = ref('')
const selectedTemplate = ref<EmailTemplate | null>(null)
const attachedDocuments = ref<AttachedDocument[]>([])

// Unsaved draft tracking
const hasUnsavedDraft = computed(() => {
  return (
    isOpen.value &&
    (emailSubject.value.trim() !== '' ||
      emailContent.value.trim() !== '' ||
      selectedTemplate.value !== null ||
      attachedDocuments.value.length > 0)
  )
})

// Initialize position to right side of screen
const initializePosition = () => {
  const screenWidth = window.innerWidth
  const screenHeight = window.innerHeight
  popoverPosition.x = screenWidth - popoverSize.width - 20 // 20px margin from right edge
  popoverPosition.y = screenHeight - popoverSize.height - 40 // 40px from bottom
}

// Initialize on script load
initializePosition()

// Setup resize handler
let resizeListener: (() => void) | null = null

// New method for atomic updates to prevent reactivity loops
const updateEmailData = (data: {
  subject?: string
  content?: string
  template?: EmailTemplate | null
  keepExistingContent?: boolean
}) => {
  // First handle the template since it might influence other values
  if (data.template !== undefined) {
    selectedTemplate.value = data.template
  }

  // Then update content if provided or if template changed and we're not keeping existing content
  if (data.content !== undefined) {
    emailContent.value = data.content
  } else if (data.template?.html && !data.keepExistingContent) {
    emailContent.value = data.template.html
  }
  emailSubject.value = data.subject ?? selectedTemplate.value?.subject ?? ''

  // Emit that the template was updated if it was included in this update
  if (data.template !== undefined) {
    emitter.emit('template-updated', data.template)
  }
}

// Create a composable to expose the state and methods
export function useEmailPopover() {
  // Lifecycle hooks for clean setup and teardown
  onMounted(() => {
    // Add resize listener if it doesn't exist
    if (!resizeListener) {
      resizeListener = () => {
        // Only reset position if the popover is not minimized or maximized
        if (isOpen.value && !isMinimized.value && !isMaximized.value) {
          initializePosition()
        }
      }
      window.addEventListener('resize', resizeListener)
    }
  })

  onUnmounted(() => {
    // Clean up resize listener
    if (resizeListener) {
      window.removeEventListener('resize', resizeListener)
      resizeListener = null
    }
  })

  // Methods to control the popover
  const openPopover = (reply: EmailReplyData | null = null) => {
    replyData.value = reply
    if (reply) {
      emailSubject.value = reply.subject
    }
    isOpen.value = true
  }

  const closePopover = () => {
    isOpen.value = false
  }

  const toggleMinimize = () => {
    isMinimized.value = !isMinimized.value
    if (isMinimized.value) {
      isMaximized.value = false
    }
  }

  const toggleMaximize = () => {
    isMaximized.value = !isMaximized.value
    if (isMaximized.value) {
      isMinimized.value = false
    }
  }

  const updatePosition = (x: number, y: number) => {
    popoverPosition.x = x
    popoverPosition.y = y
  }

  const updateSize = (width: number, height: number) => {
    popoverSize.width = width
    popoverSize.height = height
  }

  const resetPosition = () => {
    initializePosition()
  }

  // Email content methods
  const setEmailSubject = (subject: string) => {
    emailSubject.value = subject
  }

  const setEmailContent = (content: string) => {
    emailContent.value = content
  }

  const setTemplate = (template: EmailTemplate | null) => {
    // Use the atomic update to set template without triggering recursion
    updateEmailData({ template })
  }

  const addAttachedDocument = (document: AttachedDocument) => {
    // Check if document already exists by UUID or ID
    let exists = false
    if (document.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE) {
      exists = attachedDocuments.value.some(
        (doc) =>
          (doc.documentProperties as FormDocumentProperties).id === document.documentProperties.id
      )
    } else {
      exists = attachedDocuments.value.some(
        (doc) =>
          (doc.documentProperties as FormDocumentProperties).id === document.documentProperties.id
      )
    }

    if (!exists) {
      attachedDocuments.value.push(document)
    }
  }

  const removeAttachedDocument = (documentId: string) => {
    attachedDocuments.value = attachedDocuments.value.filter((doc) => {
      if (doc.documentType.id === DocumentTypeIdEnum.PDF_TEMPLATE) {
        return (doc.documentProperties as FormDocumentProperties).id !== documentId
      } else {
        return (doc.documentProperties as FormDocumentProperties).id !== documentId
      }
    })
  }

  const clearEmailData = () => {
    emailSubject.value = ''
    emailContent.value = ''
    selectedTemplate.value = null
    attachedDocuments.value = []
  }

  // Email event methods
  const emitEmailSent = (emailData: any) => {
    emitter.emit('email-sent', emailData)
  }

  const onEmailSent = (callback: (emailData: any) => void) => {
    emitter.on('email-sent', callback)
    return () => emitter.off('email-sent', callback)
  }

  // Add listener for template updates
  const onTemplateUpdated = (callback: (template: EmailTemplate | null) => void) => {
    emitter.on('template-updated', callback)
    return () => emitter.off('template-updated', callback)
  }

  return {
    // State
    isOpen,
    isMinimized,
    isMaximized,
    replyData,
    popoverPosition,
    popoverSize,

    // Email content state
    emailSubject,
    emailContent,
    selectedTemplate,
    attachedDocuments,

    // Draft state
    hasUnsavedDraft,

    // Methods
    openPopover,
    closePopover,
    toggleMinimize,
    toggleMaximize,
    updatePosition,
    updateSize,
    resetPosition,

    // Email content methods
    setEmailSubject,
    setEmailContent,
    setTemplate,
    addAttachedDocument,
    removeAttachedDocument,
    clearEmailData,
    updateEmailData,

    // Email event methods
    emitEmailSent,
    onEmailSent,
    onTemplateUpdated
  }
}
